import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { compose, withProps } from 'recompose';

import { Tooltip } from '@ontruck/orc';
import useNonPalletizedType from 'utils/hooks/useNonPalletizedType';
import { IS_TENANT } from 'tenant/config';

const defaultPalletPerCountry = {
  UK: 'US_PALLET',
};

const DEFAULT_PALLET_TYPE = 'EU_PALLET';

const enhance = compose(
  injectIntl,
  withProps(({ shipper }) => ({
    country: (shipper || {}).country,
  }))
);

const LoadSummaryCell = React.memo(
  ({
    country,
    type_pallets,
    load_types,
    intl,
    showEquivalents,
    showMax,
    real_pallets,
    max_real_pallets,
    equivalent_pallets,
    max_equivalent_pallets,
    stops,
    showJustEquivalents = false,
  }) => {
    let container;
    if (IS_TENANT) {
      container =
        stops && stops[0]
          ? [...stops[0].load_pickup, ...stops[0].load_dropoff]
              .filter((l) => l.container_iso_code)
              .reduce((p, n) => ({ ...p, ...n }), [])
          : {};
    }
    const t = intl.messages.components.LoadSummary;

    const realPallets = showMax ? max_real_pallets : real_pallets;
    const equivalentPallets = showMax ? max_equivalent_pallets : equivalent_pallets;

    const palletTypeText = {
      EU_PALLET: t.european_abbr,
      CUSTOM_PALLET: t.special_abbr,
      US_PALLET: t.american_abbr,
      MIXED_PALLET: t.mixed_abbr,
      BULK: t.bulk_abbr,
      PARCEL: t.parcel_abbr,
    };

    const defaultPallet = (!!country && defaultPalletPerCountry[country]) || DEFAULT_PALLET_TYPE;

    const { numberOfPallets, palletText, showSpecialEquivalents } = useNonPalletizedType(
      container,
      realPallets,
      load_types,
      type_pallets,
      defaultPallet
    );
    showEquivalents =
      IS_TENANT || showSpecialEquivalents ? showSpecialEquivalents : showEquivalents;

    const equivalents =
      palletText !== defaultPallet ? (
        <>
          {equivalentPallets} {palletTypeText[defaultPallet]}
        </>
      ) : null;
    const byDefault = (
      <>
        {numberOfPallets} {}
        {palletTypeText[palletText] ? palletTypeText[palletText] : palletText} {}
      </>
    );

    if (showJustEquivalents) {
      return equivalents ? (
        <Tooltip component={byDefault} position="top" skin="light">
          <span>{equivalents}*</span>
        </Tooltip>
      ) : (
        byDefault
      );
    }

    return (
      <span>
        {byDefault}
        {!!showEquivalents && !!equivalents ? <>({equivalents})</> : null}
      </span>
    );
  }
);

LoadSummaryCell.propTypes = {
  real_pallets: PropTypes.number,
  max_real_pallets: PropTypes.number,
  equivalent_pallets: PropTypes.number,
  max_equivalent_pallets: PropTypes.number,
  type_pallets: PropTypes.string,
  intl: PropTypes.object,
  showEquivalents: PropTypes.bool,
  country: PropTypes.string,
  showMax: PropTypes.bool,
};

LoadSummaryCell.defaultProps = {
  showEquivalents: false,
  showMax: false,
};

export default enhance(LoadSummaryCell);
